import React from "react";
import styled, { css } from "styled-components";

import MainThumb01 from "assets/png/landingPage/main-thumb-01.png";
import MainThumb02 from "assets/png/landingPage/main-thumb-02.png";
import MainThumb03 from "assets/png/landingPage/main-thumb-03.png";
import MainThumb04 from "assets/png/landingPage/main-thumb-04.png";
import MainThumb05 from "assets/png/landingPage/main-thumb-05.png";
import SpaceManagement from "assets/png/landingPage/space-management.png";
import TrendAnalysis from "assets/png/landingPage/trend-analysis.png";
import Writing from "assets/png/landingPage/writing.png";

import { useDevice } from "utils/device";

const DOCS_ORIGIN = "https://docs.preppaper.com";

const guideItems = [
    {
        title: "프렙 첫 시작",
        image: MainThumb01,
        links: [
            { label: "스페이스 소개", href: "/docs/prep-start/#prep-start-space" },
            { label: "참고문헌 추가 및 관리", href: "/docs/prep-start/#prep-start-reference" },
            { label: "참고문헌 리뷰 소개", href: "/docs/prep-start/#prep-start-review" },
        ],
        moreHref: "/docs/prep-start/",
    },
    {
        title: "스페이스 관리",
        image: SpaceManagement,
        links: [
            { label: "스페이스", href: "/docs/space-management/#space-management-space" },
            { label: "스페이스와 요금제", href: "/docs/space-management/#space-management-plan" },
            { label: "그룹 스페이스", href: "/docs/space-management/#space-management-group-space" },
        ],
        moreHref: "/docs/space-management/",
    },
    {
        title: "참고문헌 관리",
        image: MainThumb02,
        links: [
            { label: "참고문헌 추가하기", href: "/docs/reference-management/#reference-management-add" },
            { label: "서지정보 관리하기", href: "/docs/reference-management/#reference-management-metadata" },
            { label: "참고문헌 관리하기", href: "/docs/reference-management/#reference-management-organize" },
        ],
        moreHref: "/docs/reference-management/",
    },
    {
        title: "참고문헌 PDF 리뷰",
        image: MainThumb03,
        links: [
            { label: "시작하기", href: "/docs/reference-pdf-review/" },
            { label: "PDF 리뷰 구성", href: "/docs/reference-pdf-review/#reference-pdf-review-layout" },
            { label: "PDF 자동 분석", href: "/docs/reference-pdf-review/#reference-pdf-review-auto-analysis" },
        ],
        moreHref: "/docs/reference-pdf-review/",
    },
    {
        title: "연구노트 관리",
        image: MainThumb04,
        links: [
            { label: "새로운 연구노트 만들기", href: "/docs/research-note/#research-note-create" },
            { label: "기본 노트 기능 활용하기", href: "/docs/research-note/#research-note-basic-features" },
            { label: "AI 노트 기능 활용하기", href: "/docs/research-note/#research-note-ai-features" },
        ],
        moreHref: "/docs/research-note/",
    },
    {
        title: "동향분석",
        image: TrendAnalysis,
        links: [
            { label: "연구 주제 및 조건 입력", href: "/docs/trend-analysis/#trend-analysis-topic-input" },
            { label: "연구 계획 수립", href: "/docs/trend-analysis/#trend-analysis-research-plan" },
            { label: "연구 결과 작성", href: "/docs/trend-analysis/#trend-analysis-research-result" },
        ],
        moreHref: "/docs/trend-analysis/",
    },
    {
        title: "논문작성",
        image: Writing,
        links: [
            { label: "시작하기", href: "/docs/paper-writing/#paper-writing-start" },
            { label: "기본 작성 방법", href: "/docs/paper-writing/#paper-writing-basics" },
            { label: "자동 번호 및 참조 관리", href: "/docs/paper-writing/#paper-writing-references" },
        ],
        moreHref: "/docs/paper-writing/",
    },
    {
        title: "요금제와 결제",
        image: MainThumb05,
        links: [
            { label: "요금제 종류", href: "/docs/billing-plan/#billing-plan-types" },
            { label: "결제 정보 확인", href: "/docs/billing-plan/#billing-plan-payment-info" },
            { label: "결제하기", href: "/docs/billing-plan/#billing-plan-purchase" },
        ],
        moreHref: "/docs/billing-plan/",
    },
];

const toDocsUrl = (path) => `${DOCS_ORIGIN}${path}`;

const Container = styled.section`
    padding: 78px 0 56px;
    width: 100%;
    background: var(--color-White);
    color: var(--color-Black);
    z-index: 1;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            padding: 54px 0 32px;
        `};
`;

const Shell = styled.div`
    margin: 0 auto;
    width: min(1120px, calc(100% - 40px));
`;

const SectionTitle = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 24px;
    margin-bottom: 38px;

    &::after {
        content: "";
        height: 1px;
        background: #f1f1f1;
    }

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            display: block;
            margin-bottom: 28px;

            &::after {
                content: none;
            }
        `};
`;

const Title = styled.h2`
    margin: 0;
    font-size: 36px;
    font-weight: 700;
    line-height: 1.3;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            font-size: 30px;
        `};
`;

const GuideList = styled.div`
    display: grid;
    gap: 44px;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            gap: 34px;
        `};
`;

const GuideItem = styled.article`
    display: grid;
    grid-template-columns: minmax(260px, 0.78fr) minmax(360px, 1fr);
    align-items: center;
    gap: 42px;
    padding: 0 0 44px;
    border-bottom: 1px solid #f1f1f1;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            grid-template-columns: 1fr;
            gap: 22px;
            padding-bottom: 34px;
        `};
`;

const GuideCopy = styled.div`
    min-width: 0;
`;

const GuideTitle = styled.h3`
    margin: 0 0 22px;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.35;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            font-size: 23px;
        `};
`;

const LinkList = styled.ul`
    display: grid;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const GuideLink = styled.a`
    color: #2f3745;
    font-size: 17px;
    text-decoration: none;

    &::before {
        content: "";
        display: inline-block;
        width: 5px;
        height: 5px;
        margin-right: 10px;
        border-radius: 50%;
        background: #cbd5e1;
        vertical-align: middle;
    }

    &:hover,
    &:focus-visible {
        color: #00a878;
    }
`;

const MoreLink = styled.a`
    display: inline-flex;
    margin-top: 28px;
    color: #008464;
    font-weight: 700;
    text-decoration: none;
`;

const GuideImage = styled.img`
    display: block;
    width: 100%;
    max-width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: 8px;
    background: #f8fafc;
    box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            order: -1;
        `};
`;

function ServiceIntroSection() {
    const device = useDevice();

    return (
        <Container id="guide" $device={device}>
            <Shell>
                <SectionTitle $device={device}>
                    <Title $device={device}>사용가이드</Title>
                </SectionTitle>

                <GuideList $device={device}>
                    {guideItems.map((item) => (
                        <GuideItem key={item.title} $device={device}>
                            <GuideCopy>
                                <GuideTitle $device={device}>{item.title}</GuideTitle>
                                <LinkList>
                                    {item.links.map((link) => (
                                        <li key={link.href}>
                                            <GuideLink href={toDocsUrl(link.href)} target="_blank" rel="noreferrer">
                                                {link.label}
                                            </GuideLink>
                                        </li>
                                    ))}
                                </LinkList>
                                <MoreLink href={toDocsUrl(item.moreHref)} target="_blank" rel="noreferrer">
                                    더보기 →
                                </MoreLink>
                            </GuideCopy>
                            <GuideImage $device={device} src={item.image} alt="" />
                        </GuideItem>
                    ))}
                </GuideList>
            </Shell>
        </Container>
    );
}

export default ServiceIntroSection;
